'use client';

import React, { useState } from 'react';
import Link from 'next/link';

export default function ContactUsPage() {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      <header className="bg-white border-b border-gray-100 py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link href="/" className="font-bold text-gray-900 text-lg hover:text-red-600 transition-colors">
            ← Printeasy Hub
          </Link>
          <nav className="flex gap-4 text-sm text-gray-500">
            <Link href="/privacy-policy" className="hover:text-gray-900">Privacy</Link>
            <Link href="/terms-of-service" className="hover:text-gray-900">Terms</Link>
            <Link href="/disclaimer" className="hover:text-gray-900">Disclaimer</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-3">Contact Us</h1>
          <p className="text-gray-500 text-lg">We're here to help. Reach out via phone, email, or the form below.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-6">Get In Touch</h2>
            <div className="space-y-5">
              {[
                { icon: '📞', label: 'Phone', value: '214-852-9965', href: 'tel:+12148529965', sub: 'Mon–Sat, 9am–7pm CT' },
                { icon: '✉️', label: 'Email', value: 'info@printeasyhub.online', href: 'mailto:info@printeasyhub.online', sub: 'We reply within 24 hours' },
                { icon: '📍', label: 'Address', value: 'TX, USA', href: '#', sub: 'Serving Dallas, Houston, Austin & more' },
                { icon: '⏰', label: 'Working Hours', value: 'Monday – Saturday: 9:00 AM – 7:00 PM CT', href: '#', sub: 'Sunday: 10:00 AM – 4:00 PM CT' },
              ].map(({ icon, label, value, href, sub }) => (
                <a key={label} href={href} className="flex items-start gap-4 p-4 rounded-xl border border-gray-100 hover:shadow-md transition-shadow group">
                  <div className="text-2xl mt-0.5">{icon}</div>
                  <div>
                    <div className="font-semibold text-gray-900 text-sm">{label}</div>
                    <div className="text-gray-700 text-sm mt-0.5">{value}</div>
                    <div className="text-gray-400 text-xs mt-0.5">{sub}</div>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-8 p-5 rounded-2xl" style={{ background: 'linear-gradient(135deg, #fff5f5 0%, #ffe8e8 100%)', border: '1px solid #fecaca' }}>
              <div className="font-bold text-gray-900 mb-2">Need Urgent Help?</div>
              <p className="text-gray-600 text-sm mb-3">For same-day service or urgent printer issues, call us directly.</p>
              <a href="tel:+12148529965" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold" style={{ background: 'linear-gradient(135deg, #e63946 0%, #c1121f 100%)' }}>
                📞 Call Now: 214-852-9965
              </a>
            </div>
          </div>

          {/* Contact Form */}
          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
            {submitted ? (
              <div className="p-10 rounded-2xl border border-gray-100 text-center">
                <div className="text-5xl mb-4">✅</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Message Sent!</h3>
                <p className="text-gray-500 text-sm">Thank you for reaching out. We'll get back to you within 24 hours.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Name *</label>
                    <input
                      type="text"
                      required
                      placeholder="Your name"
                      value={formData.name}
                      onChange={e => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">Phone *</label>
                    <input
                      type="tel"
                      required
                      placeholder="(214) 555-1234"
                      value={formData.phone}
                      onChange={e => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    placeholder="your@email.com"
                    value={formData.email}
                    onChange={e => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Subject *</label>
                  <select
                    required
                    value={formData.subject}
                    onChange={e => setFormData({ ...formData, subject: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 bg-white"
                  >
                    <option value="">Select a subject</option>
                    <option value="booking">Book a Service</option>
                    <option value="inquiry">Service Inquiry</option>
                    <option value="complaint">Complaint / Feedback</option>
                    <option value="warranty">Warranty Claim</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Message *</label>
                  <textarea
                    required
                    rows={4}
                    placeholder="Describe your issue or question..."
                    value={formData.message}
                    onChange={e => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 rounded-xl text-white font-bold text-sm transition-transform hover:scale-105"
                  style={{ background: 'linear-gradient(135deg, #e63946 0%, #c1121f 100%)' }}
                >
                  Send Message →
                </button>
              </form>
            )}
          </div>
        </div>
      </main>

      <footer className="bg-gray-50 border-t border-gray-100 py-6 mt-12">
        <div className="max-w-4xl mx-auto px-6 flex flex-wrap gap-4 text-sm text-gray-500 justify-center">
          <Link href="/" className="hover:text-gray-900">Home</Link>
          <Link href="/privacy-policy" className="hover:text-gray-900">Privacy Policy</Link>
          <Link href="/terms-of-service" className="hover:text-gray-900">Terms of Service</Link>
          <Link href="/disclaimer" className="hover:text-gray-900">Disclaimer</Link>
        </div>
      </footer>
    </div>
  );
}
